import React from 'react';
import { Button, Modal } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import '../styles/modernFormModal.css';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Same header style as PO Details: light gray bar, dark text. Default is purple gradient. */
  headerVariant?: 'default' | 'light';
  /** Optional summary block below title (e.g. PO Number, Vendor, Status) – stays fixed with header */
  headerSummary?: React.ReactNode;
  width?: number | string;
  isSubmitting?: boolean;
  cancelText?: string;
  okText?: string;
  okIcon?: React.ReactNode;
  onOk?: () => void;
  okDisabled?: boolean;
  /** When false, only the cancel/close button is shown (e.g. for info or tab-based modals). */
  showOkButton?: boolean;
  /** When false, footer actions are hidden completely. */
  showFooter?: boolean;
  wrapClassName?: string;
  getContainer?: () => HTMLElement;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  destroyOnClose?: boolean;
  children: React.ReactNode;
};

const ModernFormModal: React.FC<Props> = ({
  open,
  onClose,
  title,
  subtitle,
  headerVariant = 'default',
  headerSummary,
  width = 1100,
  isSubmitting = false,
  cancelText = 'Cancel',
  okText = 'Save',
  okIcon,
  onOk,
  okDisabled,
  showOkButton = true,
  showFooter = true,
  wrapClassName = 'modern-form-modal-wrap',
  getContainer,
  style,
  bodyStyle,
  destroyOnClose = true,
  children,
}) => {
  const headerClass = headerVariant === 'light' ? 'mf-modal-header mf-modal-header--light' : 'mf-modal-header';
  return (
    <Modal
      open={open}
      onCancel={() => {
        if (isSubmitting) return;
        onClose();
      }}
      footer={null}
      className="modern-form-modal"
      wrapClassName={wrapClassName}
      getContainer={getContainer}
      style={style}
      destroyOnClose={destroyOnClose}
      centered={!getContainer}
      width={width}
      closable={false}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      bodyStyle={bodyStyle ?? { padding: 0 }}
    >
      <div className={headerClass}>
        <button
          type="button"
          className="mf-modal-close"
          onClick={() => {
            if (isSubmitting) return;
            onClose();
          }}
          aria-label="Close"
        >
          <CloseOutlined />
        </button>
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
        {headerSummary ? <div className="mf-modal-header-summary">{headerSummary}</div> : null}
      </div>

      <div className="mf-modal-body">{children}</div>

      {showFooter && (
        <div className="mf-modal-footer">
          <Button
            onClick={() => {
              if (isSubmitting) return;
              onClose();
            }}
            className="mf-cancel-btn"
            size="large"
            disabled={isSubmitting}
          >
            {cancelText}
          </Button>
          {showOkButton && (
            <Button
              type="primary"
              onClick={onOk}
              className="mf-ok-btn"
              size="large"
              loading={isSubmitting}
              disabled={!!okDisabled || isSubmitting}
            >
              {okIcon ?? <CheckOutlined />}
              {okText}
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ModernFormModal;

